import emitter from "../../../utils/bus";
import temperatureLegendUrl from '../../../images/map_style/temperature_legend.webp';
import rainfallLegendUrl from '../../../images/map_style/rainfall_legend.webp';
import snowfallLegendUrl from '../../../images/map_style/snowfall_legend.webp';
import cloudLegendUrl from '../../../images/map_style/cloud_legend.webp';
import windLegendUrl from '../../../images/map_style/wind_legend.webp';
import {MapLayerCategory} from "../../../utils/layer-registry";
import { i18n } from "../../../i18n";
import {
  getOverlayLineStyles,
  lineOpacityFor,
  scaleLineWidth,
} from "../../../utils/overlayLineStyles";

// ───────────────────────── Constants used by helpers ──────────────────────
export const HIIKER_TRAILS_SOURCE_ID = 'hiiker-trails-source';
export const HIIKER_TRAILS_MAIN_ID   = 'hiiker-trails-main-layer';
export const HIIKER_TRAILS_BORDER_ID = 'hiiker-trails-border-layer';
export const HIIKER_TRAILS_NAMES_ID  = 'hiiker-trails-names-layer';

const CELL_TOWER_SOURCE_ID = 'cell-tower-source';
const CELL_TOWER_LAYERS = [
  'cell-tower-red-circle',
  'cell-tower-white-ring-1',
  'cell-tower-white-ring-2',
  'cell-tower-white-ring-3'
];

const OPENWEATHER_API_KEY = import.meta.env.VITE_OPENWEATHER_KEY;
const OPENWEATHER_PALETTE = '0.5:1AF764;1:1ACD1A;2:1AA71A;4:1A741A;6:1A591A;8:56801A;10:F2EC1A;12:FFBC1A;14:FF821A;16:FF3E1A;24:FF1A45;32:FF1A83;60:B537BC';

// ─────────────────────────── Helper funcs ───────────────────────
export const darkenColor = (hex:string,f=0.7)=>
  '#'+(hex.match(/\w\w/g)||[]).map(c=>Math.max(0,Math.min(255,Math.floor(parseInt(c,16)*f))).toString(16).padStart(2,'0')).join('');

const legendUrl = (name:string)=>{
  switch(name){
    case 'temperature':return temperatureLegendUrl
    case 'rainfall'   :return rainfallLegendUrl
    case 'snowfall'   :return snowfallLegendUrl
    case 'cloud'      :return cloudLegendUrl
    case 'wind'       :return windLegendUrl
    default:''; } };

/* IDs */
const SLOPE_SRC  = 'slope-source';
const SLOPE_LYR  = 'slope-layer';

export function addSlope() {
  if (!window.map) return;
  window.layerRegistry.addLayer(
    {id:   SLOPE_LYR, type: 'raster', source: SLOPE_SRC, paint: { 'raster-opacity': 0.7 }},
    MapLayerCategory.Overlays,
    {
      z: 10,
      source: {
        id:   SLOPE_SRC, spec: {
          type: 'raster',
          tiles: ['https://slope.hiiker.com/slope/{z}/{x}/{y}?key=hk-slope-web-Kp7nQ2'],
          minzoom: 2,
          maxzoom: 15,
          tileSize: 256
        }
      }
    }
  );
}

export const removeSlope = () =>
  window.layerRegistry.removeLayer(SLOPE_LYR);

/* ── Community heatmap overlay ─────────────────────────────────────────────
 * Raster PNG tiles served direct from heatmap.hiiker.com behind Cloudflare —
 * pre-rendered + identical for every user, so they cache at the edge. 512px
 * tiles, world coverage at native z15 (2.39 m/px — path-readable at trail
 * zoom); mapbox overzooms/upscales past the tileset's maxzoom
 * automatically, and out-of-coverage tiles 404 cheaply with CORS set, so
 * sparse coverage is expected/normal — see 404 handling note in the module.
 * A dark full-screen `background` layer sits underneath (z:2) so the warm
 * heat colors read against a dark ground regardless of the active basemap.
 */
const HEATMAP_SRC     = 'heatmap-source';
const HEATMAP_LYR     = 'heatmap-layer';
const HEATMAP_DIM_LYR = 'heatmap-dim-layer';
const HEATMAP_TILE_URL = 'https://heatmap.hiiker.com/{z}/{x}/{y}.png';

/* Pro zoom-gate. The heatmap OVERVIEW is free (community-contributed data, the
 * shareable marketing asset); the path-readable TRAIL DETAIL past z12 is Pro+.
 *
 * We do NOT show free users an ugly upscaled blur past their z12 ceiling — that
 * reads as "broken", not "premium". Instead the overlay FADES OUT between z12
 * and z13.5 (raster + dim together) so free users drop cleanly back to a normal
 * map, and an in-map "Pro+ for trail detail" pill appears to explain why + link
 * to /purchases. Pro/Pro+ get constant opacity to native z15.
 *
 * Client-side gate for v1 (edge-token enforcement for z>=14 is the reserved
 * hardening if analytics justify it). */
export const HEATMAP_FREE_MAXZOOM = 12;
export const HEATMAP_PRO_MAXZOOM  = 15;
// Zoom at/below which the pill hides and the free overlay is at full opacity.
export const HEATMAP_GATE_ZOOM    = 12;
const HEATMAP_FADE_END            = 13.5; // free overlay fully faded by here
const HEATMAP_PILL_ID             = 'heatmap-upsell-pill';

let heatmapZoomHandler: (() => void) | null = null;

export function addHeatmap(isPro = false) {
  if (!window.map) return;

  // Free users: fade the overlay to 0 across z12 -> z13.5 rather than exposing
  // overzoom blur. Pro: constant. Expressions keep this GPU-side (no JS churn).
  const rasterOpacity = isPro
    ? 0.85
    : ['interpolate', ['linear'], ['zoom'], HEATMAP_GATE_ZOOM, 0.85, HEATMAP_FADE_END, 0];
  const dimOpacity = isPro
    ? 0.55
    : ['interpolate', ['linear'], ['zoom'], HEATMAP_GATE_ZOOM, 0.55, HEATMAP_FADE_END, 0];

  // Dim layer first so it lands below the heatmap raster (z:2 < z:3).
  window.layerRegistry.addLayer(
    {
      id: HEATMAP_DIM_LYR,
      type: 'background',
      paint: { 'background-color': '#0b0e13', 'background-opacity': dimOpacity }
    },
    MapLayerCategory.Overlays,
    { z: 2, replace: true, belowLabels: true }
  );

  window.layerRegistry.addLayer(
    { id: HEATMAP_LYR, type: 'raster', source: HEATMAP_SRC, paint: { 'raster-opacity': rasterOpacity } },
    MapLayerCategory.Overlays,
    {
      z: 3,
      source: {
        id:   HEATMAP_SRC,
        spec: {
          type: 'raster',
          tiles: [HEATMAP_TILE_URL],
          tileSize: 512,
          minzoom: 0,
          maxzoom: isPro ? HEATMAP_PRO_MAXZOOM : HEATMAP_FREE_MAXZOOM
        },
        // replace so a tier change (e.g. purchase completes mid-session)
        // re-registers the source with the new zoom ceiling
        replace: true
      },
      replace: true,
      belowLabels: true
    }
  );

  // Free users get the in-map upsell pill (shown only when zoomed past the gate).
  if (isPro) removeHeatmapUpsell();
  else       addHeatmapUpsell();
}

export const removeHeatmap = () => {
  window.layerRegistry.removeLayer(HEATMAP_LYR);
  window.layerRegistry.removeLayer(HEATMAP_DIM_LYR);
  removeHeatmapUpsell();
};

/* In-map "upgrade for detail" pill for free users. Plain DOM over the map
 * container (not a mapbox layer) so it can be a real tappable link. Visible
 * only while the map is zoomed past the free gate — i.e. exactly where the
 * overlay has faded and detail would otherwise be missing. */
function addHeatmapUpsell() {
  const map: any = window.map;
  // Guard for the unit-test map stub ({} with no getContainer/on).
  if (!map || typeof map.getContainer !== 'function' || typeof map.on !== 'function') return;

  const container = map.getContainer();
  let pill = container.querySelector('#' + HEATMAP_PILL_ID) as HTMLElement | null;
  if (!pill) {
    pill = document.createElement('button');
    pill.id = HEATMAP_PILL_ID;
    pill.type = 'button';
    pill.textContent = '🔒 ' + i18n.global.t('base_map_dialog.base_map_dialog_helpers.unlock_detailed_heatmaps');
    Object.assign(pill.style, {
      position: 'absolute', bottom: '28px', left: '50%', transform: 'translateX(-50%)',
      zIndex: '5', background: 'rgba(11,14,19,0.92)', color: '#fff',
      border: '1px solid #fddf00', borderRadius: '999px', padding: '9px 16px',
      font: '600 13px/1.25 system-ui, -apple-system, sans-serif', cursor: 'pointer',
      boxShadow: '0 2px 10px rgba(0,0,0,0.45)', display: 'none',
      maxWidth: 'calc(100% - 24px)', textAlign: 'center',
    } as CSSStyleDeclaration);
    pill.addEventListener('click', () => { window.location.href = '/purchases'; });
    container.appendChild(pill);
  }

  const update = () => {
    if (pill) pill.style.display = map.getZoom() > HEATMAP_GATE_ZOOM ? 'block' : 'none';
  };
  update();
  // Clear any prior handler before wiring a new one (idempotent re-adds).
  if (heatmapZoomHandler) map.off('zoom', heatmapZoomHandler);
  heatmapZoomHandler = update;
  map.on('zoom', update);
}

function removeHeatmapUpsell() {
  const map: any = window.map;
  if (map && heatmapZoomHandler && typeof map.off === 'function') {
    map.off('zoom', heatmapZoomHandler);
  }
  heatmapZoomHandler = null;
  const container = map && typeof map.getContainer === 'function' ? map.getContainer() : null;
  const pill = container?.querySelector('#' + HEATMAP_PILL_ID);
  if (pill) pill.remove();
}

/** The authored (1×, 100%) widths for the official-hikes overlay lines. */
const OFFICIAL_HIKES_BASE_MAIN_WIDTH = 2;
const OFFICIAL_HIKES_BASE_HALO_WIDTH = 6; // wider than main to create the halo

/** The user's customised official-hikes line style, resolved to paint values. */
export function officialHikesLinePaint() {
  const prefs = getOverlayLineStyles().officialHikes;
  return {
    color: prefs.color,
    opacity: lineOpacityFor(prefs),
    mainWidth: scaleLineWidth(OFFICIAL_HIKES_BASE_MAIN_WIDTH, prefs.thickness),
    haloWidth: scaleLineWidth(OFFICIAL_HIKES_BASE_HALO_WIDTH, prefs.thickness),
  };
}

/**
 * Re-applies the current official-hikes line style in place. Keeps the vector
 * tiles loaded rather than re-adding the source, so an opacity drag doesn't
 * re-fetch the whole overlay.
 */
export function restyleOfficialHikes() {
  const map: any = window.map;
  if (!map || typeof map.getLayer !== 'function') return;

  const paint = officialHikesLinePaint();
  if (map.getLayer(HIIKER_TRAILS_MAIN_ID)) {
    map.setPaintProperty(HIIKER_TRAILS_MAIN_ID, 'line-color', paint.color);
    map.setPaintProperty(HIIKER_TRAILS_MAIN_ID, 'line-width', paint.mainWidth);
    map.setPaintProperty(HIIKER_TRAILS_MAIN_ID, 'line-opacity', paint.opacity);
  }
  if (map.getLayer(HIIKER_TRAILS_BORDER_ID)) {
    map.setPaintProperty(HIIKER_TRAILS_BORDER_ID, 'line-width', paint.haloWidth);
    // The halo is white at half strength; fade it with the line so a
    // low-opacity line doesn't sit inside a fully-opaque outline.
    map.setPaintProperty(HIIKER_TRAILS_BORDER_ID, 'line-opacity', paint.opacity);
  }
}

export function addOfficialHikes() {
  if (!window.map) return;

  const url = window.hiiker_trails_source as string;
  if (!url) return;

  /* one shared vector source */
  window.layerRegistry.addSource(
    HIIKER_TRAILS_SOURCE_ID,
    { type: 'vector', url }
  );

  const paint = officialHikesLinePaint();

  /* MAIN coloured stroke (z:0) */
  window.layerRegistry.addLayer(
    { id: HIIKER_TRAILS_MAIN_ID, type: 'line',  source: HIIKER_TRAILS_SOURCE_ID, 'source-layer': 'data',
      paint: {
        'line-color': paint.color,
        'line-width': paint.mainWidth,
        'line-opacity': paint.opacity
      }
    },
    MapLayerCategory.Overlays,
    { z: 6 } // Keep main line above border
  );

  /* WHITE halo (z:1) */
  window.layerRegistry.addLayer(
    {
      id: HIIKER_TRAILS_BORDER_ID,
      type: 'line',
      source: HIIKER_TRAILS_SOURCE_ID,
      'source-layer': 'data',
      paint: {
        'line-color': 'rgba(255, 255, 255, 0.5)', // tweak to taste
        'line-width': paint.haloWidth,
        'line-opacity': paint.opacity
      }
    },
    MapLayerCategory.Overlays,
    { z: 1 } // Keep border below main line
  );

  /* TRAIL NAMES - Symbol layer (z:7) */
  window.layerRegistry.addLayer(
    {
      id: HIIKER_TRAILS_NAMES_ID,
      type: 'symbol',
      source: HIIKER_TRAILS_SOURCE_ID,
      'source-layer': 'data',
      minzoom: 12, // <-- Only show at zoom 12 and above
      layout: {
        'symbol-placement': 'line',
        'text-field': ['get', 'name'], // Get text from the 'name' property
        'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'], // Specify font(s)
        'text-size': 12,                // Adjust size as needed
        'text-allow-overlap': false,    // Prevent labels overlapping each other
        'symbol-spacing': 250,          // Distance between labels on the line
        'text-max-angle': 30,           // Max angle for labels on curves (degrees)
        'text-pitch-alignment': 'map',  // Keep text flat on the map surface
      },
      paint: {
        'text-color': '#333333',        // Dark text color
        'text-halo-color': 'rgba(255, 255, 255, 0.8)', // White halo for contrast
        'text-halo-width': 1.5
      }
    },
    MapLayerCategory.Overlays, // Or MapLayerCategory.Annotations if preferred
    { z: 7 } // Draw names above the main line
  );
}

export function removeOfficialHikes() {
  if (!window.map) return;
  window.layerRegistry.removeLayer(HIIKER_TRAILS_BORDER_ID);
  window.layerRegistry.removeLayer(HIIKER_TRAILS_MAIN_ID);
  window.layerRegistry.removeLayer(HIIKER_TRAILS_NAMES_ID);
}

export function addCell() {
  if (!window.map) return;

  window.layerRegistry.addSource(
    CELL_TOWER_SOURCE_ID,
    { type: 'vector', url: 'mapbox://hiiker-maps.global_cell_tower_data' }
  );

  const ringPaint =
    { 'circle-color': 'transparent',
      'circle-stroke-color': '#fff',
      'circle-stroke-width': 1 };

  [
    { id: CELL_TOWER_LAYERS[3], r: 12 },
    { id: CELL_TOWER_LAYERS[2], r: 9  },
    { id: CELL_TOWER_LAYERS[1], r: 6  },
    { id: CELL_TOWER_LAYERS[0], r: 3, fill: '#DB2B35' }     // core
  ].forEach(({ id, r, fill }, z) => {
    window.layerRegistry.addLayer(
      {
        id,
        type: 'circle',
        source: CELL_TOWER_SOURCE_ID,
        'source-layer': 'data',
        paint: fill
          ? { 'circle-radius': r, 'circle-color': fill }
          : { ...ringPaint, 'circle-radius': r }
      },
      MapLayerCategory.Annotations,
      { z }                      // inner > outer
    );
  });
}

export const removeCell = () =>
  CELL_TOWER_LAYERS.forEach(id => window.layerRegistry.removeLayer(id));


export function addWeather(type: string, name: string) {
  if (!window.map) return;

  const srcId   = `${name}-source`;
  const layerId = `${name}-layer`;
  const base    = `https://maps.openweathermap.org/maps/2.0/weather/${type}/{z}/{x}/{y}?appid=${OPENWEATHER_API_KEY}`;
  const url     =
    name === 'rainfall' || name === 'snowfall'
      ? `${base}&palette=${OPENWEATHER_PALETTE}`
      :  base;

  window.layerRegistry.addLayer(
    {
      id: layerId,
      type: 'raster',
      source: srcId,
      paint: { 'raster-opacity': 0.6 }
    },
    MapLayerCategory.Overlays,
    {
      z: 5,
      source: {
        id:   srcId,
        spec: { type: 'raster', tiles: [url], tileSize: 256 }
      }
    }
  );

  /* legend DOM logic unchanged */
  removeWeatherLegend(layerId);
  const lg = legendUrl(name);
  lg && addWeatherLegend({ url: lg, id: layerId });
}

export const removeWeather = (name: string) => {
  const layerId = `${name}-layer`;
  window.layerRegistry.removeLayer(layerId);
  removeWeatherLegend(layerId);
};


function addWeatherLegend(legendData: { url: string, id: string }) {
  const legendContainer = document.getElementById('weather-legend-container');
  if (legendContainer) {
    const legendImage = document.createElement('img');
    legendImage.src = legendData.url;
    legendImage.id = legendData.id;
    legendContainer.appendChild(legendImage);
  } else {
    console.error("Weather legend container not found.");
  }
}
function  removeWeatherLegend(legendId: string) {
  const legendContainer = document.getElementById('weather-legend-container');
  //empty container
  if (legendContainer) {
    legendContainer.innerHTML = '';
  }
}

// ─────────────────────── Cycling Routes overlay ───────────────────────
export const CYCLING_SOURCE_ID = 'cycling-routes-source';
export const CYCLING_BORDER_ID = 'cycling-border-layer';
export const CYCLING_NCN_ID   = 'cycling-ncn-layer';
export const CYCLING_LINK_ID  = 'cycling-link-layer';

const CYCLING_LAYERS = [CYCLING_BORDER_ID, CYCLING_NCN_ID, CYCLING_LINK_ID];

const CYCLING_COLOR_EXPR = [
  'case',
  ['==', ['get', 'Desc_'], 'TrafficFree'], '#FFA500',
  ['==', ['get', 'Desc_'], 'OnRoad'],      '#000080',
  '#000000'
] as any;

export function addCyclingRoutes() {
  if (!window.map) return;

  window.layerRegistry.addSource(
    CYCLING_SOURCE_ID,
    { type: 'vector', url: 'mapbox://hiiker-maps.0silg7di' }
  );

  // White halo beneath both route types (z: 2)
  window.layerRegistry.addLayer(
    {
      id: CYCLING_BORDER_ID,
      type: 'line',
      source: CYCLING_SOURCE_ID,
      'source-layer': 'National_Cycle_Network_Public-0ul9o8',
      paint: { 'line-color': 'rgba(255,255,255,0.6)', 'line-width': 5 }
    },
    MapLayerCategory.Overlays,
    { z: 2 }
  );

  // NCN solid lines (z: 3)
  window.layerRegistry.addLayer(
    {
      id: CYCLING_NCN_ID,
      type: 'line',
      source: CYCLING_SOURCE_ID,
      'source-layer': 'National_Cycle_Network_Public-0ul9o8',
      filter: ['==', ['get', 'RouteType'], 'NCN'],
      paint: { 'line-color': CYCLING_COLOR_EXPR, 'line-width': 2 }
    },
    MapLayerCategory.Overlays,
    { z: 3 }
  );

  // LINK dashed lines (z: 4) — separate layer because line-dasharray is not data-driven
  window.layerRegistry.addLayer(
    {
      id: CYCLING_LINK_ID,
      type: 'line',
      source: CYCLING_SOURCE_ID,
      'source-layer': 'National_Cycle_Network_Public-0ul9o8',
      filter: ['==', ['get', 'RouteType'], 'LINK'],
      paint: {
        'line-color': CYCLING_COLOR_EXPR,
        'line-width': 2,
        'line-dasharray': [2, 2]
      }
    },
    MapLayerCategory.Overlays,
    { z: 4 }
  );
}

export function removeCyclingRoutes() {
  if (!window.map) return;
  CYCLING_LAYERS.forEach(id => window.layerRegistry.removeLayer(id));
}

// Helper function to safely parse JSON from localStorage
export const safeJsonParse = (key: string, defaultValue: any) => {
  const item = localStorage.getItem(key);
  if (item) {
    try {
      // Special handling for boolean "false" which is truthy as a string
      if (item === 'false') return false;
      // Special handling for boolean "true" which is truthy as a string
      if (item === 'true') return true;
      // Handle null
      if (item === 'null') return null;
      // Attempt to parse other JSON
      return JSON.parse(item);
    } catch (e) {
      console.error(`Error parsing localStorage key "${key}":`, e, `Raw value: "${item}"`);
      localStorage.removeItem(key); // Remove invalid data
      return defaultValue;
    }
  }
  return defaultValue;
};
